import { RuleStyle } from "@/shared/domain/layouts/LayoutRecord"; // Needs to be a larger concept than layout record
import { State, File, Datasource } from "@/shared/state/visualization";
import { assignSeriesColor } from "@/shared/state/visualization/assignSeriesColor";
import { cyclingColorScale } from "@/shared/state/visualization/CyclingColorScale";
import {
  idForClip,
  idForSeries,
  idForMap,
} from "@/shared/state/visualization/identifiers";
import { PanelType, TopicData } from "@/shared/state/visualization/schema";
import {
  DocumentPanelData,
  ImagePanelClip,
  MapPath,
  PanelStateByType,
  PanelStates,
  PlotSeries,
  ThreeDPanelData,
} from "@/shared/state/visualization/schema/v1";

import { VizConfig } from "./VizConfig";

export class VizConfigBuilder {
  #state: State;

  public static transformTopicDataForPanel<T extends PanelType>(
    panelType: T,
    panelId: string,
    data: { data: TopicData[]; style: RuleStyle }[],
  ): PanelStateByType<T>["data"] {
    return panelDataTransformers[panelType](data, panelId);
  }

  constructor() {
    this.#state = VizConfig.default();
  }

  public addFile(file: File): this {
    this.#state.files.push(file);
    return this;
  }

  public addPanel(panel: PanelStates): this {
    if (this.#state.panels === undefined) {
      this.#state.panels = {
        [panel.id]: panel,
      };
      return this;
    }

    this.#state.panels[panel.id] = panel;
    return this;
  }

  public setFiles(files: File[]): this {
    this.#state.files = files;
    return this;
  }

  public setDatasources(datasources: Datasource[]): this {
    this.#state.datasources = datasources;
    return this;
  }

  public setLayout(layout: State["layout"]): this {
    this.#state.layout = layout;
    return this;
  }

  public setPanels(panels: State["panels"]): this {
    this.#state.panels = panels;
    return this;
  }

  public build(): VizConfig {
    return new VizConfig(this.#state);
  }
}

type TopicDataWithStyle = { data: TopicData[]; style: RuleStyle };

interface PanelDataTransformer {
  Document: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => DocumentPanelData | null;
  Log: (topicDataList: TopicDataWithStyle[], panelId: string) => TopicData[];
  Image: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => ImagePanelClip[];
  Plot: (topicDataList: TopicDataWithStyle[], panelId: string) => PlotSeries[];
  Map: (topicDataList: TopicDataWithStyle[], panelId: string) => MapPath[];
  ThreeD: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => ThreeDPanelData;
  RawMessage: (
    topicDataList: TopicDataWithStyle[],
    panelId: string,
  ) => TopicData[];
}

/**
 * Converts a structured list of {@link TopicData} into a function that can be used to populate a panel's data.
 *
 * The reason for the 2-D list is because some panel types (Map panel) expect multiple TopicData objects
 * in its data field.
 *
 */
const panelDataTransformers: PanelDataTransformer = {
  Document: () => null, // Document panel doesn't use topic data
  Log: (topicDataList) => {
    // Log panel does not support data from different topics or chunked bags (ROBO-1281)
    // So we will just take the data that matches the topicId of the first match
    const topicData = topicDataList.flatMap((item) => item.data);
    const firstMatch = topicData[0];

    if (firstMatch === undefined) {
      return [];
    }

    return topicData.filter((data) => data.topic.id === firstMatch.topic.id);
  },
  Image: (topicDataList) =>
    topicDataList
      .flatMap((item) => item.data)
      .map((data) => ({ id: idForClip(), data })),
  Plot: (topicDataList, panelId) => {
    const series: PlotSeries[] = [];
    topicDataList.forEach((item) => {
      // Add a new series for each entry in item.data but with the rule's style
      item.data.forEach((data) => {
        series.push({
          id: idForSeries(),
          data,
          visible: true,
          config: {
            lineColor: item.style?.color ?? assignSeriesColor(panelId, data),
            lineWidth: item.style?.lineWidth,
            pointRadius: item.style?.pointRadius,
            yAxis: item.style?.yAxis,
            label: item.style?.label,
            transform: item.style?.transform,
          },
        });
      });
    });
    return series;
  },
  Map: (topicDataList, panelId) =>
    topicDataList.map((item) => ({
      id: idForMap(item.data),
      data: item.data,
      visible: true,
      style: {
        lineColor:
          item.style?.color ?? cyclingColorScale.getNextColorForPanel(panelId),
      },
    })),
  ThreeD: (topicDataList) => {
    const data: ThreeDPanelData = {
      entities: {},
    };
    for (const topicData of topicDataList) {
      const config = topicData.style.threeDComponents;
      if (!config) {
        continue;
      }
      data.entities[config.entityId] ??= { id: config.entityId };
      const entity = data.entities[config.entityId];
      if (config.position) {
        entity.position = {
          transformation: config.position.transformation,
          basePreset: config.position.basePreset,
          right:
            config.position.right.type === "fixed"
              ? config.position.right.value
              : topicData.data[config.position.right.topicDataIndex],
          rightScale: config.position.right.scale,
          forward:
            config.position.forward.type === "fixed"
              ? config.position.forward.value
              : topicData.data[config.position.forward.topicDataIndex],
          forwardScale: config.position.forward.scale,
          up:
            config.position.up.type === "fixed"
              ? config.position.up.value
              : topicData.data[config.position.up.topicDataIndex],
          upScale: config.position.up.scale,
        };
      }
      if (config.mesh) {
        entity.mesh = config.mesh;
      }
      if (config.path) {
        entity.path = config.path;
      }
    }
    return data;
  },
  RawMessage: (topicDataList) => topicDataList.flatMap((item) => item.data),
};
